import moment from "moment";

import type { GetMediaResponse } from "../api/MediaAPI.types";
import type { LiveStreamTypes } from "../models/LiveStream";

export interface LiveSettings {
  inputType: LiveStreamTypes;
  maxResolution: string;
  durationSeconds: number;
  scheduled: boolean;
  scheduledStart: string | null;
  instantReplay: boolean;
}

const DEFAULT_START_DELAY_MINUTES = 10;

export function readLiveSettings(item: GetMediaResponse): LiveSettings {
  const liveStream = item.live_stream;

  return {
    inputType: liveStream?.manifest?.input_type ?? "rtmp",
    maxResolution: liveStream?.manifest?.max_resolution ?? "360",
    durationSeconds: item.duration,
    scheduled: liveStream?.status === "scheduled",
    scheduledStart: liveStream?.live_start ?? null,
    instantReplay: liveStream?.instant_replay ?? true,
  };
}

interface ApplyLiveSettingsOptions {
  /** organization_options.allow_replay: without it instant_replay is always false. */
  allowReplay: boolean;
}

/**
 * Serializes editable live settings only. Outside preparation (status other
 * than none/scheduled) nothing is written and the same item is returned.
 */
export function applyLiveSettings(
  item: GetMediaResponse,
  patch: Partial<LiveSettings>,
  { allowReplay }: ApplyLiveSettingsOptions,
): GetMediaResponse {
  const status = item.live_stream?.status ?? "none";

  if (status !== "none" && status !== "scheduled") return item;

  const current = readLiveSettings(item);
  const next = { ...current, ...patch };
  const liveStream = item.live_stream;

  if (!liveStream) return { ...item, duration: next.durationSeconds };

  const base = {
    ...liveStream,
    instant_replay: allowReplay && next.instantReplay,
    manifest: {
      ...liveStream.manifest,
      max_resolution: next.maxResolution,
      input_type: next.inputType,
    },
  };

  // The backend expects live_start/live_end in every preparation payload:
  // null when the live is not scheduled.
  if (!next.scheduled) {
    return {
      ...item,
      duration: next.durationSeconds,
      live_stream: {
        ...base,
        status: "none",
        live_start: null,
        live_end: null,
      },
    };
  }

  const liveStart =
    next.scheduledStart ??
    moment.utc().add(DEFAULT_START_DELAY_MINUTES, "minutes").format();
  const liveEnd = moment
    .utc(liveStart)
    .add(next.durationSeconds, "seconds")
    .format();

  return {
    ...item,
    duration: next.durationSeconds,
    live_stream: {
      ...base,
      status: "scheduled",
      live_start: liveStart,
      live_end: liveEnd,
    },
  };
}
